/**
 * System → Background jobs + ingestion tab (brief §6.7.2).
 *
 * Lists registered cron jobs with their schedule, last-run metadata, and a
 * "Run now" button per row. Filters: status (success/failed/running) +
 * search. Read-only otherwise — the only mutation is run-now, which writes
 * a cron_runs row + an admin_audit_log row server-side.
 *
 * Auto-refreshes every 15s via TanStack Query so a long-running job's status
 * updates without manual reload. The detail of a failed run is shown inline
 * in the row (status badge tooltip + the action error banner below the
 * filters).
 */

import { useMemo, useState } from 'react';
import { Play } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isReadOnlyAdminMode } from '@/lib/admin-mode';
import { IngestionSummary } from './IngestionSummary';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import {
  isProduction,
  useJobs,
  useJobStatus,
  useRunJobNow,
  type CronWorkerStatus,
  type JobRow,
} from './api';
import {
  DASH,
  DataTable,
  DataTableShell,
  FilterSelect,
  formatTimestamp,
  timeAgo,
  timeUntil,
} from './shared';

type StatusFilter = 'any' | 'success' | 'failed' | 'running';

export function JobsTab() {
  const { data, isLoading, error } = useJobs();
  const telemetry = useJobStatus();
  const readOnly = isReadOnlyAdminMode();
  const runNow = useRunJobNow();

  const [q, setQ] = useState('');
  const [status, setStatus] = useState<StatusFilter>('any');
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmJob, setConfirmJob] = useState<JobRow | null>(null);

  const allRows: JobRow[] = useMemo(() => data?.data ?? [], [data]);

  const rows = useMemo(() => {
    return allRows.filter((row) => {
      if (status !== 'any') {
        const lastStatus = row.last_run?.status ?? null;
        if (lastStatus !== status) return false;
      }
      if (q) {
        const needle = q.toLowerCase();
        const haystack =
          `${row.name} ${row.schedule} ${row.description}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [allRows, status, q]);

  const simulationMode = data?.meta.simulation_mode ?? 'unknown';
  const workers = telemetry.data?.workers;
  const cronWorker = workers?.find(w => w.registered_jobs.includes('executeMatchSchedule')) ?? workers?.[0] ?? data?.meta.cron_worker ?? null;

  function requestRunNow(row: JobRow) {
    if (readOnly || !row.enabled) return;
    if (isProduction()) {
      setConfirmJob(row);
      return;
    }
    void handleRunNow(row.name);
  }

  async function handleRunNow(name: string) {
    if (readOnly) return;
    setActionError(null);
    try {
      await runNow.mutateAsync(name);
    } catch (err) {
      setActionError(
        `Run-now failed for ${name}: ${err instanceof Error ? err.message : 'unknown error'}`,
      );
    }
  }

  return (
    <div className="space-y-4">
      {error || telemetry.error && telemetry.data ? <p role="status" className="text-sm text-amber-700">Refresh failed. Any retained evidence is a previous snapshot, not a current health confirmation.</p> : null}
      <IngestionSummary rows={allRows} worker={cronWorker} telemetry={telemetry.data} simulationMode={simulationMode} />
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[280px] flex-1">
          <Input
            placeholder="Search by name, schedule, or description…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <FilterSelect
          label="Last run"
          value={status}
          onChange={(v) => setStatus(v as StatusFilter)}
          options={[
            { value: 'any', label: 'Any' },
            { value: 'success', label: 'Success' },
            { value: 'failed', label: 'Failed' },
            { value: 'running', label: 'Running' },
          ]}
        />
      </div>

      <div className="rounded-md border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">Simulation mode: {simulationMode}.</span>{' '}
        API manual permission is separate from worker registration. Calculated due times are not
        execution receipts. The preview refreshes job evidence every 15 seconds; extended data evidence every 30 seconds.
      </div>

      <CronWorkerCard worker={cronWorker} />

      {actionError ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-2 text-sm text-destructive">
          {actionError}
        </div>
      ) : null}

      <DataTableShell loading={isLoading} error={error} empty={rows.length === 0}>
        <DataTable<JobRow>
          rows={rows}
          rowKey={(r) => r.name}
          columns={[
            {
              key: 'name',
              label: 'Job',
              render: (r) => (
                <div>
                  <div className="font-mono font-medium text-foreground">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.description}</div>
                </div>
              ),
            },
            {
              key: 'schedule',
              label: 'Configured cadence (UTC)',
              render: (r) => <ScheduleCell row={r} />,
            },
            {
              key: 'enabled',
              label: 'API manual permission',
              render: (r) => <ManualRunCell row={r} simulationMode={simulationMode} />,
            },
            {
              key: 'last_run',
              label: 'Last run',
              render: (r) => <LastRunCell row={r} />,
            },
            {
              key: 'next_run_at',
              label: 'Calculated due time',
              render: (r) => (
                <NextAutomaticRunCell
                  row={r}
                  simulationMode={simulationMode}
                  workerStatus={cronWorker?.status ?? 'unknown'}
                />
              ),
            },
            {
              key: 'actions',
              label: '',
              align: 'right',
              render: (r) => (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={readOnly || !r.enabled || (runNow.isPending && runNow.variables === r.name)}
                  title={readOnly ? 'This preview cannot trigger jobs' : !r.enabled ? 'Disabled in the current runtime mode' : undefined}
                  onClick={() => requestRunNow(r)}
                >
                  <Play className="h-3.5 w-3.5" />
                  {readOnly ? 'Read-only' : r.enabled ? 'Run now' : 'Blocked'}
                </Button>
              ),
            },
          ]}
        />
      </DataTableShell>

      <RunNowConfirmDialog
        job={confirmJob}
        pending={runNow.isPending}
        onCancel={() => setConfirmJob(null)}
        onConfirm={async () => {
          if (!confirmJob) return;
          await handleRunNow(confirmJob.name);
          setConfirmJob(null);
        }}
      />
    </div>
  );
}

interface RunNowConfirmDialogProps {
  job: JobRow | null;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

function RunNowConfirmDialog({
  job,
  pending,
  onCancel,
  onConfirm,
}: RunNowConfirmDialogProps) {
  return (
    <Dialog open={!!job} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Run production job now?</DialogTitle>
          <DialogDescription>
            This will execute the job immediately against the production database.
          </DialogDescription>
        </DialogHeader>

        {job ? (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border bg-muted/30 p-3">
              <div className="font-mono font-medium">{job.name}</div>
              <div className="mt-1 text-muted-foreground">{job.description}</div>
            </div>
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
              Manual runs can fetch external data, update fixture/player state, and trigger
              downstream scoring or audit entries. Only continue if this is expected right now.
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void onConfirm()} disabled={pending}>
            {pending ? 'Running...' : 'Run job'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleCell({ row }: { row: JobRow }) {
  const schedule = describeSchedule(row);

  return (
    <div className="text-sm">
      <div>{schedule.label}</div>
      {schedule.detail ? (
        <div className="mt-1 max-w-[32ch] text-xs text-muted-foreground">{schedule.detail}</div>
      ) : null}
      <div className="mt-1 font-mono text-xs text-muted-foreground">
        Cron: {row.schedule}
      </div>
    </div>
  );
}

function CronWorkerCard({ worker }: { worker: CronWorkerStatus | null }) {
  const status = worker?.status ?? 'unknown';
  const badge =
    status === 'online' ? (
      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
        Recent heartbeat
      </Badge>
    ) : status === 'stale' ? (
      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
        Stale
      </Badge>
    ) : status === 'offline' ? (
      <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
        Offline
      </Badge>
    ) : (
      <Badge variant="outline">Unknown</Badge>
    );

  return (
    <div className="grid gap-3 rounded-md border bg-card px-4 py-4 text-sm md:grid-cols-[minmax(220px,0.9fr)_minmax(0,1.6fr)_minmax(180px,0.7fr)]">
      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Ingestion cron worker
        </div>
        <div className="mt-2 flex items-center gap-2">
          {badge}
          <span className="font-mono text-xs text-muted-foreground">
            {worker?.worker_id ?? 'ingestion-cron'}
          </span>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          Automatic jobs
        </div>
        <div className="mt-2 text-foreground">
          {status === 'online'
            ? 'Worker heartbeat observed; per-job registration and outcome still need verification.'
            : status === 'stale'
              ? 'Treat automatic schedules as at risk until the heartbeat refreshes.'
              : status === 'offline'
                ? 'No recent heartbeat reported for this worker ID; this does not prove all workers are stopped.'
                : 'Worker status cannot be confirmed yet.'}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">Heartbeat scope: {worker?.worker_id ?? 'unknown'}. Older APIs may only inspect the default ingestion-cron ID.</div>
        {worker?.registered_jobs.length ? <div className="mt-1 break-words text-xs text-muted-foreground">Worker reports registered: {worker.registered_jobs.filter((n): n is string => typeof n === 'string').join(', ')}</div> : null}
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Last heartbeat</div>
        <div className="mt-2 text-foreground">
          {worker?.last_seen_at ? timeAgo(worker.last_seen_at) : DASH}
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {worker?.last_seen_at ? formatTimestamp(worker.last_seen_at) : 'No heartbeat recorded'}
        </div>
      </div>
    </div>
  );
}

function ManualRunCell({ row, simulationMode }: { row: JobRow; simulationMode: string }) {
  if (row.enabled) {
    return (
      <div className="space-y-1">
        <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
          Allowed
        </Badge>
        <div className="text-xs text-muted-foreground">API permits manual execution; preview policy may block it.</div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Badge variant="outline" className="border-zinc-200 bg-zinc-50 text-zinc-600">
        Blocked
      </Badge>
      <div className="text-xs text-muted-foreground">Blocked by runtime gate ({simulationMode}).</div>
    </div>
  );
}

function NextAutomaticRunCell({
  row,
  simulationMode,
  workerStatus,
}: {
  row: JobRow;
  simulationMode: string;
  workerStatus: string;
}) {
  if (!row.enabled || row.schedule_enabled === false) {
    return (
      <div className="text-sm">
        <div>No automatic due time</div>
        <div className="text-xs text-muted-foreground">
          Automatic schedule disabled in the current runtime ({simulationMode}). Manual permission is separate.
        </div>
      </div>
    );
  }

  if (!row.next_run_at) return <span className="text-muted-foreground">Unknown</span>;

  return (
    <div className="text-sm">
      <div>{timeUntil(row.next_run_at)}</div>
      <div className="text-xs text-muted-foreground">{formatTimestamp(row.next_run_at)}</div>
      <div className="mt-1 max-w-[24ch] text-xs text-muted-foreground">
        {automaticRunNote(row, workerStatus)}
      </div>
    </div>
  );
}

function LastRunCell({ row }: { row: JobRow }) {
  const last = row.last_run;
  if (!last) return <span className="text-muted-foreground">{DASH}</span>;

  const badge =
    last.status === 'success' ? (
      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
        Success
      </Badge>
    ) : last.status === 'failed' ? (
      <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">
        Failed
      </Badge>
    ) : last.status === 'running' ? (
      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
        Running
      </Badge>
    ) : (
      <Badge variant="outline">{last.status}</Badge>
    );

  return (
    <div className="space-y-1 text-sm">
      <div className="flex items-center gap-2">
        {badge}
        {last.triggered_by === 'admin' ? (
          <span className="text-xs uppercase tracking-wide text-muted-foreground">manual</span>
        ) : null}
      </div>
      <div className="text-xs text-muted-foreground" title={formatTimestamp(last.started_at)}>
        {timeAgo(last.started_at)}
      </div>
      {last.error ? (
        <div className="max-w-[40ch] truncate text-xs text-destructive" title={last.error}>
          {last.error}
        </div>
      ) : null}
    </div>
  );
}

function describeSchedule(row: JobRow): { label: string; detail?: string } {
  if (row.name === 'syncAllPlayers' && row.schedule === '0 6 * * 1') {
    return { label: 'Weekly, Monday 06:00 UTC' };
  }
  if (row.name === 'syncAllFixtures' && row.schedule === '0 */6 * * *') {
    return { label: 'Every 6 hours' };
  }
  if (row.name === 'executeMatchSchedule') {
    return {
      label: row.schedule === '*/5 * * * *' ? 'Checks every 5 minutes' : row.schedule === '* * * * *' ? 'Checks every minute' : row.schedule,
      detail: 'Internal lifecycle / scoring check. Provider live sync may be disabled by the runtime profile.',
    };
  }
  return { label: row.schedule };
}

function automaticRunNote(row: JobRow, workerStatus: string): string {
  if (row.name === 'executeMatchSchedule') {
    if (workerStatus === 'online') {
      return 'Calculated due time, not a run receipt. Heartbeat alone does not confirm this job is registered or made changes.';
    }
    return 'Calculated only; automatic execution cannot be confirmed from this heartbeat.';
  }

  if (workerStatus === 'online') {
    return 'Calculated only; confirm worker registration and an observed run.';
  }
  return 'Calculated only; worker execution is unconfirmed.';
}
